/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Buffer } from 'buffer';

import { Endpoint } from '../index';
import { z, ZodTypes } from '../zod';

export const UserGet = {
  id: 'user.get',
  tag: 'User',
  summary: 'Get one user',
  description: 'Get user details',
  method: 'get',
  flags: { isAuthorized: true },
  route: () => `/user`,
  response: {
    200: {
      description: 'successful',
      content: {
        'application/json': {
          schema: z.strictObject({
            _links: z.strictObject({
              self: z.strictObject({ href: z.string() }),
              thumbnail: z.strictObject({ href: z.string() }).optional(),
            }),
            id: ZodTypes.Id,
            authentication: z.discriminatedUnion('type', [
              z.strictObject({ type: z.literal('local') }),
              z.strictObject({ type: z.literal('oauth2'), oauth2Id: z.string(), provider: ZodTypes.OAuth2Providers }),
              z.strictObject({ type: z.literal('oidc'), oidcId: z.string(), groups: z.array(z.string()) }),
            ]),
            createdAt: z.date().optional(),
            updatedAt: z.date().optional(),
            email: z.string().email(),
            firstName: z.string(),
            middleName: z.string().nullable().optional(),
            lastName: z.string(),
            verified: z.boolean(),
            phoneNationalNumber: z.string().min(1).nullable().optional(),
            phoneCountryCode: ZodTypes.Country.nullable().optional(),
            language: ZodTypes.Language,
            ratingLevel: z.string().nullable().optional(),
            marketable: z.boolean(),
            disableDynamicAllocation: z.boolean(),
            billingReady: z.boolean(),
            invitations: z.array(
              z.strictObject({
                id: ZodTypes.Id,
                createdAt: z.coerce.date(),
                organization: z.strictObject({
                  id: ZodTypes.Id,
                  name: ZodTypes.KubernetesIdentifier,
                  companyName: z.string().min(1),
                }),
              }),
            ),
            isSuperuser: z.boolean().optional(),
            isDisabled: z.boolean().optional(),
            notificationSettings: z
              .strictObject({
                notification: ZodTypes.Notification,
              })
              .array()
              .optional(),
            address: z
              .strictObject({
                id: ZodTypes.Id,
                line1: z.string().min(1),
                line2: z.string().nullable().optional(),
                line3: z.string().nullable().optional(),
                postalCode: z.string().min(1),
                city: z.string().min(1),
                state: z.string().nullable().optional(),
                countryCode: ZodTypes.Country,
              })
              .nullable()
              .optional(),
            thumbnail: z
              .object({
                id: ZodTypes.Id,
                image: z.instanceof(Buffer).nullable().optional(),
                seed: ZodTypes.Base64.nullable().optional(),
              })
              .nullable()
              .optional(),
            organizations: z.array(
              z.strictObject({
                id: ZodTypes.Id,
                roles: z.array(ZodTypes.RBAC.Organization.Roles),
                projects: z.array(
                  z.strictObject({
                    id: ZodTypes.Id,
                    roles: z.array(ZodTypes.RBAC.Project.Roles),
                  }),
                ),
              }),
            ),
          }),
        },
      },
    },
  },
} as const satisfies Endpoint;
